import React, { useCallback, useState } from 'react';
import {
  Badge,
  Button,
  EmptyCell,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2, RefreshCw, Search, Send, UserPlus } from 'lucide-react';
import { FeatureFlag } from '../../constants/featureFlags';
import { useStore } from '../../store';
import type { OrgMemberResponse, RoleInfo } from './types';
import type { RogerDocClinicianAccount } from '../../services/accountLinkApi';
import { filterUserVisibleRoles } from '../../utils/rbacRoles';
import { formatDateTimeInPT } from '../../utils/dateUtils';

// ─── ClinicBadges ─────────────────────────────────────────────────────────────

const ClinicBadges: React.FC<{
  assignments: OrgMemberResponse['clinic_assignments'];
  hiddenClinicIds?: ReadonlySet<string>;
}> = React.memo(function ClinicBadges({ assignments, hiddenClinicIds }) {
  const list = (assignments ?? []).filter(
    assignment => !hiddenClinicIds?.has(assignment.clinic_id)
  );
  if (list.length === 0) {
    return <EmptyCell />;
  }
  return (
    <div className='flex gap-1 flex-wrap'>
      {list.map(a => (
        <Badge
          key={a.clinic_id}
          variant='outline'
          className='text-xs whitespace-nowrap'
        >
          {a.clinic_name}
        </Badge>
      ))}
    </div>
  );
});

// ─── RbacRoleBadges ──────────────────────────────────────────────────────────

const RbacRoleBadges: React.FC<{ roles: RoleInfo[] | undefined }> = React.memo(
  function RbacRoleBadges({ roles }) {
    const roleList = filterUserVisibleRoles(roles ?? []);
    if (roleList.length === 0) {
      return <EmptyCell />;
    }
    return (
      <div className='flex gap-1 flex-wrap'>
        {roleList.map(r => (
          <Badge
            key={r.id}
            variant='outline'
            className='text-xs whitespace-nowrap'
          >
            {r.name}
          </Badge>
        ))}
      </div>
    );
  }
);

// ─── RogerDocAccountBadges ───────────────────────────────────────────────────

const RogerDocAccountBadges: React.FC<{
  accounts: RogerDocClinicianAccount[] | undefined;
}> = React.memo(function RogerDocAccountBadges({ accounts }) {
  const list = accounts ?? [];
  if (list.length === 0) {
    return <EmptyCell />;
  }
  return (
    <div className='flex gap-1 flex-wrap'>
      {list.map(a => (
        <Badge
          key={a.id}
          variant='secondary'
          className='text-xs whitespace-nowrap'
        >
          {a.username}
        </Badge>
      ))}
    </div>
  );
});

// ─── LastLoginCell ───────────────────────────────────────────────────────────

const LastLoginCell: React.FC<{
  isPending: boolean;
  lastLogin: string | null | undefined;
}> = React.memo(function LastLoginCell({ isPending, lastLogin }) {
  // A pending invitee hasn't accepted THIS agency's invite yet — the Name cell's
  // Pending badge already carries that state, so we mask any last_login with the
  // table's em-dash. last_login is now scoped to the current agency, but a member
  // pending here may still have a non-null last_login from another agency, so this
  // deliberately keys on is_pending first.
  if (isPending) {
    return <EmptyCell />;
  }
  // Accepted but never signed in: the row GTM most needs to spot.
  if (!lastLogin) {
    return <span className='text-sm text-muted-foreground'>Never</span>;
  }
  return <span className='text-sm'>{formatDateTimeInPT(lastLogin)}</span>;
});

// ─── MemberRow ───────────────────────────────────────────────────────────────

type MemberRowProps = {
  member: OrgMemberResponse;
  onClick: (member: OrgMemberResponse) => void;
  showClinicInfoInUserManager: boolean;
  hiddenClinicIds?: ReadonlySet<string>;
  rogerDocAccounts: RogerDocClinicianAccount[] | undefined;
  canResendInvite: boolean;
  isResendingInvite: boolean;
  onResendInvite: (member: OrgMemberResponse) => void;
};

const MemberRow: React.FC<MemberRowProps> = React.memo(function MemberRow({
  member,
  onClick,
  showClinicInfoInUserManager,
  hiddenClinicIds,
  rogerDocAccounts,
  canResendInvite,
  isResendingInvite,
  onResendInvite,
}) {
  return (
    <TableRow className='cursor-pointer' onClick={() => onClick(member)}>
      <TableCell className='font-medium'>
        <div className='flex items-center gap-2'>
          {member.first_name} {member.last_name}
          {member.is_pending && (
            <>
              <Badge variant='secondary' className='text-xs'>
                Pending
              </Badge>
              {canResendInvite && (
                <Button
                  variant='ghost'
                  size='sm'
                  disabled={isResendingInvite}
                  onClick={e => {
                    e.stopPropagation();
                    onResendInvite(member);
                  }}
                >
                  {isResendingInvite ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Send className='h-3.5 w-3.5' />
                  )}
                  Resend invite
                </Button>
              )}
            </>
          )}
        </div>
      </TableCell>

      <TableCell className='text-muted-foreground'>{member.email}</TableCell>

      <TableCell>
        <LastLoginCell
          isPending={member.is_pending ?? false}
          lastLogin={member.last_login}
        />
      </TableCell>

      {showClinicInfoInUserManager && (
        <TableCell>
          <ClinicBadges
            assignments={member.clinic_assignments}
            hiddenClinicIds={hiddenClinicIds}
          />
        </TableCell>
      )}

      <TableCell>
        <RogerDocAccountBadges accounts={rogerDocAccounts} />
      </TableCell>

      <TableCell>
        <RbacRoleBadges roles={member.role_assignments} />
      </TableCell>
    </TableRow>
  );
});

// ─── MembersTable ────────────────────────────────────────────────────────────

type MembersTableProps = {
  members: OrgMemberResponse[];
  isLoading: boolean;
  hasSearchQuery: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onRefresh: () => void;
  onEditMember: (member: OrgMemberResponse) => void;
  onInvite: () => void;
  onResendInvite: (member: OrgMemberResponse) => Promise<void> | void;
  canInvite: boolean;
  rogerDocLinksByEmail?: Record<string, RogerDocClinicianAccount[]>;
  hiddenClinicIds?: ReadonlySet<string>;
};

export const MembersTable: React.FC<MembersTableProps> = React.memo(
  function MembersTable({
    members,
    isLoading,
    hasSearchQuery,
    searchQuery,
    onSearchChange,
    onRefresh,
    onEditMember,
    onInvite,
    onResendInvite,
    canInvite,
    rogerDocLinksByEmail = {},
    hiddenClinicIds,
  }) {
    const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
    const showClinicInfoInUserManager = hasFeatureFlag(
      FeatureFlag.SHOW_CLINIC_INFO_IN_USER_MANAGER
    );

    // Track which pending members' invites are currently being resent so we can
    // show a per-row spinner and prevent duplicate clicks. A set (not a single
    // value) so overlapping resends on different rows stay independent.
    const [resendingEmails, setResendingEmails] = useState<Set<string>>(
      new Set()
    );
    const handleResendInvite = useCallback(
      async (member: OrgMemberResponse) => {
        setResendingEmails(prev => new Set(prev).add(member.email));
        try {
          await onResendInvite(member);
        } finally {
          setResendingEmails(prev => {
            const next = new Set(prev);
            next.delete(member.email);
            return next;
          });
        }
      },
      [onResendInvite]
    );

    return (
      <>
        {/* Header */}
        <div className='flex flex-col gap-3 md:flex-row md:justify-between md:items-center'>
          <h1 className='text-2xl font-bold tracking-tight'>User Manager</h1>
          <div className='flex flex-wrap gap-2'>
            <Button variant='outline' onClick={onRefresh} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <RefreshCw className='h-4 w-4' />
              )}
              Refresh
            </Button>
            {canInvite && (
              <Button onClick={onInvite} disabled={isLoading}>
                <UserPlus className='h-4 w-4' />
                Invite User
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className='flex flex-wrap gap-4 items-end'>
          <div className='flex-1 min-w-[200px] max-w-sm'>
            <label
              htmlFor='member-search'
              className='text-sm font-medium mb-1.5 block'
            >
              Search
            </label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                id='member-search'
                placeholder='Search by name or email...'
                value={searchQuery}
                onChange={e => onSearchChange(e.target.value)}
                className='pl-9'
              />
            </div>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            <span className='ml-3 text-muted-foreground'>
              Loading organization members...
            </span>
          </div>
        ) : members.length === 0 ? (
          <div className='text-center text-muted-foreground py-8'>
            <p>
              {hasSearchQuery
                ? 'No members match your search.'
                : 'No organization members found.'}
            </p>
          </div>
        ) : (
          <div className='rounded-md border'>
            {/* min-width keeps columns from collapsing on mobile (where the
                role/clinic badge columns would otherwise be starved of width
                and wrap into very tall rows); the table scrolls horizontally
                instead, giving the badge columns a desktop-like share so rows
                keep a normal height. No effect on desktop, where the full
                width already exceeds this minimum. */}
            <Table className='min-w-[900px]'>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead title='Last login to this agency'>
                    Last Login
                  </TableHead>
                  {showClinicInfoInUserManager && (
                    <TableHead>Clinics With Roles</TableHead>
                  )}
                  <TableHead>RogerDoc User</TableHead>
                  <TableHead>Agency Level Roles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map(member => (
                  <MemberRow
                    key={member.workos_user_id}
                    member={member}
                    onClick={onEditMember}
                    showClinicInfoInUserManager={showClinicInfoInUserManager}
                    hiddenClinicIds={hiddenClinicIds}
                    rogerDocAccounts={
                      rogerDocLinksByEmail[member.email.toLowerCase()]
                    }
                    canResendInvite={canInvite}
                    isResendingInvite={resendingEmails.has(member.email)}
                    onResendInvite={handleResendInvite}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </>
    );
  }
);
